import React from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { FaCheckCircle, FaShieldAlt, FaDatabase, FaExclamationTriangle } from "react-icons/fa";
import styles from "./Pages.module.css";

function RansomwareProtection() {
  return (
    <section className={styles.pageSection}>
      <Helmet>
        <title>Ransomware Protection for Shreveport Small Businesses | Tharpe Industries</title>
        <meta
          name="description"
          content="Ransomware attacks hit small businesses every day. Learn how to protect your Shreveport-Bossier business with practical, affordable security steps."
        />
        <meta
          name="keywords"
          content="ransomware protection Shreveport, small business security, ransomware defense, business backup, IT security Shreveport"
        />
        <link rel="canonical" href="https://tharpeindustries.com/ransomware-protection" />
      </Helmet>

      <h2>Ransomware Doesn't Just Target Big Companies</h2>
      
      <p>
        When most people think about ransomware, they picture huge corporations paying million-dollar ransoms. But the truth is simpler and scarier: small businesses are common targets, maybe more common than large ones. Why? Because you're less likely to have robust security and backups in place. A ransomware attacker knows a small business will panic fast and pay up.
      </p>

      <p>
        The good news: you don't need enterprise-level security to protect yourself. You need the basics, done right.
      </p>

      <h3>What Actually Happens When Ransomware Hits</h3>
      <p>
        Here's the plain-English version: A malicious file gets onto your computer, usually through a phishing email, a sketchy download, or an unpatched vulnerability. Once it's in, it encrypts your files. You can't open them. Your business stops. Then you get a message: "Your files are encrypted. Send us money in Bitcoin and I'll give you the key."
      </p>
      <p>
        Some businesses pay. Most who do never get their files back anyway. The smarter move is making sure you never get into that situation in the first place.
      </p>

      <h3>Three Things That Actually Protect You</h3>
      <div className={styles.contentContainer}>
        <div className={styles.textContent}>
          <div style={{ marginBottom: "1.5rem" }}>
            <h4 style={{ marginBottom: "0.5rem", color: "var(--primary)" }}>
              <FaDatabase style={{ marginRight: "0.5rem", color: "var(--accent-orange)" }} aria-hidden="true" />
              Working Backups You Can Actually Restore From
            </h4>
            <p style={{ marginLeft: "1.75rem" }}>
              The single best defense against ransomware is a backup that actually works. Not just "we have backup software running." I mean a backup you've tested, one you know you can restore from if everything else breaks. If ransomware encrypts your files, you restore from backup, pay zero dollars, and move on.
            </p>
          </div>

          <div style={{ marginBottom: "1.5rem" }}>
            <h4 style={{ marginBottom: "0.5rem", color: "var(--primary)" }}>
              <FaShieldAlt style={{ marginRight: "0.5rem", color: "var(--accent-orange)" }} aria-hidden="true" />
              Keeping Systems Patched and Updated
            </h4>
            <p style={{ marginLeft: "1.75rem" }}>
              A huge chunk of ransomware attacks exploit security holes in Windows, Adobe, or other software. Those holes get patched, but only if you actually install the updates. Yes, updates are annoying. They're also the difference between "patched" and "vulnerable."
            </p>
          </div>

          <div>
            <h4 style={{ marginBottom: "0.5rem", color: "var(--primary)" }}>
              <FaExclamationTriangle style={{ marginRight: "0.5rem", color: "var(--accent-orange)" }} aria-hidden="true" />
              Basic Employee Awareness
            </h4>
            <p style={{ marginLeft: "1.75rem" }}>
              Most ransomware doesn't sneak in silently. Someone clicks a link in a phishing email. Someone downloads an attachment from a sketchy sender. Your team doesn't need to be paranoid, but they do need basic awareness: "Does this email look weird? Am I expecting this link? If not, don't click." Our <Link to="/cybersecurity">cybersecurity services</Link> include employee awareness training, teaching your team to spot phishing and social engineering tricks.
            </p>
          </div>
        </div>
      </div>

      <h3>The Honest Take</h3>
      <p>
        If you're reading this, you're probably not sure where you stand on these three things. You might have backups but never tested them. You might be patching sometimes but not consistently. You might have no formal security training for your team.
      </p>
      <p>
        That's exactly what the IT Health Check is designed to answer. I'll check your backup status, review your security basics, and tell you exactly where you're exposed. No sales pitch. Just honest answers about what needs to happen.
      </p>

      <div className={styles.relatedPages}>
        <p className={styles.relatedPagesHeading}>Related resources:</p>
        <div className={styles.relatedPageLinks}>
          <Link to="/cybersecurity">Cybersecurity Services</Link>
          <Link to="/data-backup-recovery">Data Backup & Recovery</Link>
          <Link to="/how-to-choose-it-support">How to Choose IT Support</Link>
        </div>
      </div>

      <div className={styles.ctaContainer}>
        <p><strong>Start with the $149 IT Health Check</strong></p>
        <p className={styles.emergencyText}>I'll review your backup setup and security basics, then send you a clear report showing exactly where you stand.</p>
        <Link to="/it-health-check" className={styles.ctaButton}>Book the IT Health Check</Link>
      </div>

      <div className={styles.secondaryCta}>
        <p>Have specific ransomware concerns? <Link to="/contact-us">Send us a message</Link> and we'll help you think through your situation.</p>
      </div>

      <div className={styles.resourceLinks}>
        <p style={{ fontSize: "0.9rem", color: "var(--neutral-600)", marginBottom: "0.5rem" }}>
          Related: <Link to="/managed-it-vs-break-fix">Managed IT vs. Break-Fix</Link> · <Link to="/how-to-choose-it-support">How to Choose an IT Provider</Link>
        </p>
      </div>
    </section>
  );
}

export default RansomwareProtection;
